// pages/AdminEvents.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enLocale from '@fullcalendar/core/locales/en-gb';
import axios from 'axios';
import { PlusIcon, XMarkIcon, CalendarIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { Calendar, Clock, Globe, Lock, Trash2, Edit, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';

interface EventData {
  _id?: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social' | 'conference' | 'training' | 'webinar' | 'networking' | 'other';
  isPublic: boolean;
  color: string;
  creator?: { _id: string; name: string; email: string };
  approvalStatus?: 'pending' | 'approved' | 'rejected';
}

const EVENT_TYPES = ['meeting', 'workshop', 'deadline', 'social', 'conference', 'training', 'webinar', 'networking', 'other'] as const;
const EVENT_TYPE_LABELS: Record<string, string> = {
  meeting: 'Meeting', workshop: 'Workshop', deadline: 'Deadline', social: 'Social Event',
  conference: 'Conference', training: 'Training', webinar: 'Webinar', networking: 'Networking', other: 'Other',
};
const EVENT_TYPE_COLORS: Record<string, string> = {
  meeting: '#3788d8', workshop: '#56cc9d', deadline: '#ff9f40', social: '#9333ea',
  conference: '#0ea5e9', training: '#14b8a6', webinar: '#f59e0b', networking: '#ec4899', other: '#64748b',
};

interface FormData {
  title: string;
  description: string;
  start: string;
  end: string;
  type: typeof EVENT_TYPES[number];
  isPublic: boolean;
}

const API_BASE = API_BASE_URL;

const AdminEvents: React.FC = () => {
  const user = useSelector((state: RootState) => selectUser(state));
  const [events, setEvents] = useState<EventData[]>([]);
  const [pendingEvents, setPendingEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPendingEvents, setShowPendingEvents] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventData | null>(null);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({ title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true });

  useEffect(() => {
    const fetchEvents = async () => {
      if (!user?._id) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/events/all`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (res.data.success) {
          setEvents(res.data.data || []);
          setError(null);
        } else {
          setError(res.data.error || 'Error loading events');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Error loading events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [user?._id]);

  const fetchPendingEvents = async () => {
    if (!user?._id) return;
    setPendingLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/events/pending`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.data.success) {
        setPendingEvents(res.data.data || []);
        setError(null);
      } else {
        setError(res.data.error || 'Error loading pending events');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error loading pending events');
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    if (showPendingEvents && user?._id) fetchPendingEvents();
  }, [showPendingEvents, user?._id]);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id) { setError('User not logged in'); return; }
    if (!formData.start || !formData.end) { setError('Start and end dates are required'); return; }
    const startDate = new Date(formData.start);
    const endDate = new Date(formData.end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) { setError('Invalid date format'); return; }
    if (endDate <= startDate) { setError('End date must be after start date'); return; }

    setLoading(true);
    setError(null);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', (formData.description || '').trim());
      formDataToSend.append('start', startDate.toISOString());
      formDataToSend.append('end', endDate.toISOString());
      formDataToSend.append('type', formData.type);
      formDataToSend.append('isPublic', String(Boolean(formData.isPublic)));
      formDataToSend.append('creatorId', user._id);
      photoFiles.forEach(f => formDataToSend.append('images', f));

      const res = await axios.post(`${API_BASE}/events`, formDataToSend, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 });
      if (res.data.success) {
        setEvents(prev => [...prev, res.data.data]);
        setShowCreateModal(false);
        resetForm();
        setError(null);
        if (res.data.data.approvalStatus === 'pending' && showPendingEvents) fetchPendingEvents();
      } else {
        setError(res.data.error || 'Error creating event');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error creating event');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent?._id || !user?._id) { setError('Missing event or user'); return; }
    if (!formData.start || !formData.end) { setError('Start and end dates are required'); return; }
    const startDate = new Date(formData.start);
    const endDate = new Date(formData.end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) { setError('Invalid date format'); return; }
    if (endDate <= startDate) { setError('End date must be after start date'); return; }

    setLoading(true);
    setError(null);
    try {
      const payload = { title: formData.title.trim(), description: (formData.description || '').trim(), start: startDate.toISOString(), end: endDate.toISOString(), type: formData.type, isPublic: Boolean(formData.isPublic), creatorId: user._id };
      const res = await axios.put(`${API_BASE}/events/${editingEvent._id}`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } });
      if (res.data.success) {
        let updatedEvent = res.data.data;
        if (photoFiles.length > 0) {
          try {
            const fd = new FormData();
            photoFiles.forEach(f => fd.append('images', f));
            const uploadRes = await axios.post(`${API_BASE}/events/${editingEvent._id}/upload-image`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 });
            if (uploadRes.data?.success && uploadRes.data?.data?.event) updatedEvent = uploadRes.data.data.event;
          } catch (uploadErr: any) {
            setError(uploadErr.response?.data?.error || 'Error uploading image');
          }
        }
        setEvents(prev => prev.map(e => e._id === editingEvent._id ? updatedEvent : e));
        setEditingEvent(null);
        setShowCreateModal(false);
        resetForm();
        setError(null);
      } else {
        setError(res.data.error || 'Error updating event');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error updating event');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE}/events/${eventId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setEvents(prev => prev.filter(e => e._id !== eventId));
      setPendingEvents(prev => prev.filter(e => e._id !== eventId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error deleting event');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveEvent = async (eventId: string) => {
    setPendingLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/events/${eventId}/approve`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.data.success) {
        setPendingEvents(prev => prev.filter(e => e._id !== eventId));
        setEvents(prev => { const idx = prev.findIndex(e => e._id === eventId); return idx >= 0 ? prev.map((e, i) => i === idx ? res.data.data : e) : [...prev, res.data.data]; });
        const allRes = await axios.get(`${API_BASE}/events/all`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (allRes.data.success) setEvents(allRes.data.data || []);
        setError(null);
      } else setError(res.data.error || 'Error approving event');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error approving event');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleRejectEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to reject this event?')) return;
    setPendingLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/events/${eventId}/reject`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.data.success) {
        setPendingEvents(prev => prev.filter(e => e._id !== eventId));
        setEvents(prev => prev.filter(e => e._id !== eventId));
        const allRes = await axios.get(`${API_BASE}/events/all`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (allRes.data.success) setEvents(allRes.data.data || []);
        setError(null);
      } else setError(res.data.error || 'Error rejecting event');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error rejecting event');
    } finally {
      setPendingLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true });
    setEditingEvent(null);
    setPhotoFiles([]);
    setPhotoPreviews([]);
  };

  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
    if (files.length > 0) {
      setPhotoFiles(prev => [...prev, ...files]);
      setError(null);
      files.forEach(f => { const r = new FileReader(); r.onloadend = () => setPhotoPreviews(prev => [...prev, r.result as string]); r.readAsDataURL(f); });
    }
    (e.target as HTMLInputElement).value = '';
  }, []);

  const handleRemovePhoto = useCallback((index: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type: inputType } = e.target;
    setFormData(prev => ({ ...prev, [name]: inputType === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  }, []);

  const handleEditEvent = (event: EventData) => {
    setEditingEvent(event);
    const fmt = (d: string) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}T${String(x.getHours()).padStart(2,'0')}:${String(x.getMinutes()).padStart(2,'0')}`; };
    setFormData({ title: event.title, description: event.description || '', start: fmt(event.start), end: fmt(event.end), type: event.type, isPublic: event.isPublic });
    setShowCreateModal(true);
  };

  const calendarEvents = useMemo(() => events.map(e => ({
    id: e._id, title: e.title, start: e.start, end: e.end, backgroundColor: e.color, borderColor: e.color, textColor: '#ffffff',
    extendedProps: { description: e.description, type: e.type, isPublic: e.isPublic, creator: e.creator }
  })), [events]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  const formatTime = (dateString: string) => new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const getTypeLabel = (type: string) => EVENT_TYPE_LABELS[type] || type;

  if (loading && events.length === 0) {
    return (
      <div className="flex justify-center h-64 items-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Events Management</h1>
          <p className="text-gray-500 mt-1">Manage and approve platform events</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => { setShowPendingEvents(!showPendingEvents); if (!showPendingEvents) fetchPendingEvents(); }}
            className={`flex items-center px-4 py-2 border rounded-lg transition ${showPendingEvents ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'border-gray-300 hover:bg-gray-50'}`}>
            <AlertCircle className="w-5 h-5 mr-2" />
            Pending Events
            {pendingEvents.length > 0 && <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">{pendingEvents.length}</span>}
          </button>
          <button onClick={() => setViewMode(prev => prev === 'calendar' ? 'list' : 'calendar')} className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            {viewMode === 'calendar' ? (<><ListBulletIcon className="w-5 h-5 mr-2" />List View</>) : (<><CalendarIcon className="w-5 h-5 mr-2" />Calendar View</>)}
          </button>
          <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm">
            <PlusIcon className="w-5 h-5 mr-2" />Create Event
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800 ml-4"><XMarkIcon className="w-5 h-5" /></button>
        </div>
      )}

      {showPendingEvents && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-yellow-500" />Pending Events
              <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">{pendingEvents.length}</span>
            </h2>
            <button onClick={fetchPendingEvents} disabled={pendingLoading} className="text-sm text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
              {pendingLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          {pendingLoading && pendingEvents.length === 0 ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : pendingEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No pending events</p>
              <p className="text-xs text-gray-400 mt-2">All events have been reviewed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingEvents.map(event => (
                <div key={event._id} className={`border-2 rounded-xl p-4 ${new Date(event.end) < new Date() ? 'border-gray-200 opacity-75' : 'border-yellow-200 bg-yellow-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-1 h-12 rounded-full" style={{ backgroundColor: event.color }} />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                          {event.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <div className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatDate(event.start)} {formatTime(event.start)}</div>
                            <div className="flex items-center"><Clock className="w-4 h-4 mr-1" />{formatDate(event.end)} {formatTime(event.end)}</div>
                            {event.creator && <div className="text-xs text-gray-500">Created by: {event.creator.name}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => event._id && handleApproveEvent(event._id)} disabled={pendingLoading} className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </button>
                      <button onClick={() => event._id && handleRejectEvent(event._id)} disabled={pendingLoading} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                        <XCircle className="w-4 h-4 mr-1" />Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
          <FullCalendar plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} initialView="dayGridMonth"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
            events={calendarEvents} locale={enLocale} eventClick={(info) => { const ev = events.find(e => e._id === info.event.id); if (ev) handleEditEvent(ev); }} height="auto" eventDisplay="block" />
        </div>
      )}

      {viewMode === 'list' && (
        <div className="space-y-4">
          {events.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
              <Calendar className="h-20 w-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No events</h3>
              <p className="text-gray-500 mb-6">Create your first event to get started</p>
              <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm">
                <PlusIcon className="w-5 h-5 mr-2" />Create Event
              </button>
            </div>
          ) : (
            events.map(event => (
              <div key={event._id} className={`bg-white rounded-2xl shadow-lg hover:shadow-xl border transition-all duration-300 overflow-hidden ${new Date(event.end) < new Date() ? 'border-gray-200 opacity-75' : 'border-gray-100 hover:border-emerald-200'}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-1 h-full min-h-[80px] rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">{getTypeLabel(event.type)}</span>
                          {event.approvalStatus === 'pending' && <span className="flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs font-semibold"><AlertCircle className="h-3 w-3 mr-1" />Pending Approval</span>}
                          {event.isPublic ? <span className="flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs border border-blue-100"><Globe className="h-3 w-3 mr-1" />Public</span> : <span className="flex items-center px-2 py-1 bg-gray-50 text-gray-700 rounded-lg text-xs border border-gray-200"><Lock className="h-3 w-3 mr-1" />Private</span>}
                        </div>
                        {event.description && <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center text-gray-700">
                            <Calendar className="h-4 w-4 mr-2 text-emerald-500 flex-shrink-0" />
                            <div><p className="text-xs text-gray-500">Start</p><p className="text-sm font-medium">{formatDate(event.start)} {formatTime(event.start)}</p></div>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Calendar className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
                            <div><p className="text-xs text-gray-500">End</p><p className="text-sm font-medium">{formatDate(event.end)} {formatTime(event.end)}</p></div>
                          </div>
                        </div>
                        {event.creator && (
                          <div className="flex items-center text-gray-600 pt-3 border-t border-gray-100">
                            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mr-2">
                              <span className="text-xs font-bold text-white">{event.creator.name?.charAt(0).toUpperCase()}</span>
                            </div>
                            <span className="text-sm">Created by {event.creator.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button onClick={() => handleEditEvent(event)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Edit"><Edit className="h-5 w-5" /></button>
                      <button onClick={() => event._id && handleDeleteEvent(event._id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="h-5 w-5" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-200 p-4 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-800">{editingEvent ? 'Edit Event' : 'Create Event'}</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-6 h-6" /></button>
            </div>
            <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="p-6 space-y-4">
              <input name="title" placeholder="Event title" required value={formData.title} onChange={handleFormChange} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" />
              <textarea name="description" placeholder="Description" value={formData.description} onChange={handleFormChange} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors h-20" />
              <input type="datetime-local" name="start" required value={formData.start} onChange={handleFormChange} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" />
              <input type="datetime-local" name="end" required value={formData.end} onChange={handleFormChange} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" />
              <select name="type" value={formData.type} onChange={handleFormChange} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors">
                {EVENT_TYPES.map(type => <option key={type} value={type}>{getTypeLabel(type)}</option>)}
              </select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
                {photoPreviews.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-2">
                    {photoPreviews.map((src, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-300">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => handleRemovePhoto(i)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><XMarkIcon className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-emerald-400 transition-colors">
                  <input type="file" accept="image/*" multiple onChange={handlePhotoChange} className="hidden" id="event-photo-upload-admin" />
                  <label htmlFor="event-photo-upload-admin" className="cursor-pointer text-emerald-600 hover:text-emerald-700 font-medium">Add Photos</label>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF, WebP up to 5MB each</p>
                </div>
              </div>
              <label className="flex items-center space-x-2">
                <input name="isPublic" type="checkbox" checked={formData.isPublic} onChange={handleFormChange} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <span className="text-sm text-gray-700">Make public</span>
              </label>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm">
                  {loading ? (editingEvent ? 'Updating...' : 'Creating...') : (editingEvent ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
